import { FC, useMemo, useState } from "react";
import { TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import { CustomModal } from "../../../components/CustomModal/CustomModal";
import { CustomCheckBox } from "../../../components/CustomCheckBox/CustomCheckBox";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import {
  FleetReportFormat,
  FleetReportSectionKey,
  GenerateFleetReportPayload,
  useAvailableReportSections,
  useGenerateFleetReport,
} from "../../../api/fleetReports";
import useStyles from "./styles";

type Props = {
  open: boolean;
  onClose: () => void;
  fleetId: number;
};

/**
 * Per-section user-facing metadata (label + short description). Keys must
 * match the identifiers returned by GET /fleets/{fleetId}/reports/sections.
 *
 * Kept as a plain map, not a translated one, because these labels are also
 * used as the sheet titles by the backend. If the backend labels ever
 * become translated, this should move to i18n keys (and the backend should
 * accept a Content-Language). For now, English matches the server output.
 */
const SECTION_META: Record<
  FleetReportSectionKey,
  { label: string; description: string }
> = {
  fleet_overview: {
    label: "Fleet overview",
    description:
      "Key figures: total vehicles, status split, monthly / yearly cost, active drivers & contracts.",
  },
  vehicles_by_status: {
    label: "Vehicles by status",
    description:
      "Full inventory with current status, driver, department and monthly cost.",
  },
  incidents: {
    label: "Incidents",
    description:
      "All sinisters recorded on the fleet's vehicles. Filterable by date range below.",
  },
  costs: {
    label: "Costs",
    description:
      "Per-vehicle monthly, yearly and contract-total costs, plus a fleet-wide summary row.",
  },
  contracts_ending: {
    label: "Contracts ending",
    description:
      "Rental contracts whose end_date falls within the lookahead window (default 90 days).",
  },
  energy_mix: {
    label: "Energy mix",
    description: "Fuel-type breakdown of the fleet with count and share.",
  },
};

/**
 * All sections toggled on by default. Fleet managers typically want a full
 * report and unchecking is faster than checking six items one by one.
 */
const DEFAULT_SELECTION: FleetReportSectionKey[] = [
  "fleet_overview",
  "vehicles_by_status",
  "incidents",
  "costs",
  "contracts_ending",
  "energy_mix",
];

/**
 * Fleet report generation modal.
 *
 * UX rules:
 *  - At least one section must be selected before the Generate button
 *    enables. This is enforced client-side to avoid a 422 round trip.
 *  - Choosing CSV surfaces a warning: CSV is a flat format so the report
 *    will be a concatenation, not one-file-per-section. The user is not
 *    blocked (some workflows need CSV) but they know what they're getting.
 *  - The contract lookahead input is only visible when that section is
 *    ticked — the same for the date range which only impacts incidents.
 *    Hiding irrelevant inputs keeps the form scannable.
 */
export const FleetReportModal: FC<Props> = ({ open, onClose, fleetId }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { sections: availableSections } = useAvailableReportSections(fleetId);
  const generate = useGenerateFleetReport(fleetId);

  const [selected, setSelected] = useState<FleetReportSectionKey[]>(
    DEFAULT_SELECTION
  );
  const [format, setFormat] = useState<FleetReportFormat>("xlsx");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [contractDays, setContractDays] = useState<string>("90");

  /**
   * The catalog is fetched from the backend so unavailable sections (e.g.
   * behind a feature flag one day) never appear. Fall back to the frontend
   * defaults while loading to avoid a jumping UI.
   */
  const sectionsToRender: FleetReportSectionKey[] = useMemo(() => {
    if (availableSections.length === 0) return DEFAULT_SELECTION;
    // Filter to only those we know how to describe; unknown identifiers get
    // dropped silently rather than shown as a checkbox with no label.
    return availableSections.filter(
      (k): k is FleetReportSectionKey => k in SECTION_META
    );
  }, [availableSections]);

  const toggleSection = (key: FleetReportSectionKey) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const selectAll = () => setSelected(sectionsToRender);
  const selectNone = () => setSelected([]);

  const showIncidentFilters = selected.includes("incidents");
  const showContractLookahead = selected.includes("contracts_ending");

  const hasValidDateRange =
    !dateFrom || !dateTo || new Date(dateFrom) <= new Date(dateTo);
  const canGenerate =
    selected.length > 0 && hasValidDateRange && !generate.isPending;

  const handleGenerate = async () => {
    if (!canGenerate) return;

    const payload: GenerateFleetReportPayload = {
      format,
      sections: selected,
    };
    // Only send filters that are actually populated so the backend receives
    // a clean payload (avoids validation churn on empty strings).
    if (showIncidentFilters && dateFrom) payload.date_from = dateFrom;
    if (showIncidentFilters && dateTo) payload.date_to = dateTo;
    if (showContractLookahead && contractDays) {
      const n = parseInt(contractDays, 10);
      if (!Number.isNaN(n) && n > 0) payload.contract_ending_days = n;
    }

    try {
      await generate.mutateAsync(payload);
      onClose();
    } catch {
      // Toast already handled inside the hook; keep the modal open so the
      // user can adjust and retry without redoing the whole checklist.
    }
  };

  const handleClose = () => {
    if (generate.isPending) return; // Don't dismiss during download
    onClose();
  };

  return (
    <CustomModal
      title={t("fleetReports.title", "Generate a fleet report")}
      open={open}
      onClose={handleClose}
      onCancel={handleClose}
      onSave={handleGenerate}
      // Override the default "Save" button label — this modal produces a
      // downloadable file, not a persisted resource.
      labelConfirmation={t("fleetReports.generate", "Generate")}
      disabledSaveButton={!canGenerate}
      loadingSave={generate.isPending}
      width={"620px"}
    >
      <div className={classes.content}>
        <p className={classes.intro}>
          {t(
            "fleetReports.intro",
            "Select the sections you want to include. XLSX puts each section on its own sheet; CSV concatenates them into a single file."
          )}
        </p>

        {/* Section checklist */}
        <div className={classes.sectionBlock}>
          <div className={classes.sectionHeader}>
            <span className={classes.sectionTitle}>
              {t("fleetReports.sections", "Sections")}
            </span>
            <button
              type="button"
              className={classes.toggleAll}
              onClick={
                selected.length === sectionsToRender.length
                  ? selectNone
                  : selectAll
              }
            >
              {selected.length === sectionsToRender.length
                ? t("fleetReports.selectNone", "Unselect all")
                : t("fleetReports.selectAll", "Select all")}
            </button>
          </div>

          <div className={classes.sectionsList}>
            {sectionsToRender.map((key) => {
              const meta = SECTION_META[key];
              const checked = selected.includes(key);
              return (
                <label key={key} className={classes.sectionRow}>
                  <CustomCheckBox
                    checked={checked}
                    onChange={() => toggleSection(key)}
                  />
                  <div className={classes.sectionRowLabel}>
                    <span className={classes.sectionRowTitle}>
                      {meta.label}
                    </span>
                    <span className={classes.sectionRowDesc}>
                      {meta.description}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Contextual filters — only shown when the relevant section is on */}
        {(showIncidentFilters || showContractLookahead) && (
          <div className={classes.sectionBlock}>
            <span className={classes.sectionTitle}>
              {t("fleetReports.filters", "Filters")}
            </span>
            <div className={classes.filtersGrid}>
              {showIncidentFilters && (
                <>
                  <TextField
                    label={t("fleetReports.dateFrom", "Incidents from")}
                    type="date"
                    size="small"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={!hasValidDateRange}
                  />
                  <TextField
                    label={t("fleetReports.dateTo", "Incidents to")}
                    type="date"
                    size="small"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={!hasValidDateRange}
                    helperText={
                      !hasValidDateRange
                        ? t(
                            "fleetReports.dateRangeError",
                            "End date must be on or after start date"
                          )
                        : undefined
                    }
                  />
                </>
              )}
              {showContractLookahead && (
                <TextField
                  label={t(
                    "fleetReports.contractLookahead",
                    "Contracts ending in the next (days)"
                  )}
                  type="number"
                  size="small"
                  value={contractDays}
                  onChange={(e) => setContractDays(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 1, max: 3650 }}
                />
              )}
            </div>
          </div>
        )}

        {/* Format selector */}
        <div className={classes.sectionBlock}>
          <span className={classes.sectionTitle}>
            {t("fleetReports.format", "Format")}
          </span>
          <div className={classes.formatSelector}>
            <div
              className={`${classes.formatOption} ${
                format === "xlsx" ? classes.formatOptionActive : ""
              }`}
              onClick={() => setFormat("xlsx")}
              role="radio"
              aria-checked={format === "xlsx"}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setFormat("xlsx");
              }}
            >
              <span className={classes.formatOptionLabel}>Excel (.xlsx)</span>
              <span className={classes.formatOptionHint}>
                {t(
                  "fleetReports.xlsxHint",
                  "One sheet per section — best for browsing"
                )}
              </span>
            </div>
            <div
              className={`${classes.formatOption} ${
                format === "csv" ? classes.formatOptionActive : ""
              }`}
              onClick={() => setFormat("csv")}
              role="radio"
              aria-checked={format === "csv"}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setFormat("csv");
              }}
            >
              <span className={classes.formatOptionLabel}>CSV (.csv)</span>
              <span className={classes.formatOptionHint}>
                {t(
                  "fleetReports.csvHint",
                  "Flat file — best for import into another tool"
                )}
              </span>
            </div>
          </div>
          {format === "csv" && selected.length > 1 && (
            <div className={classes.formatWarning}>
              {t(
                "fleetReports.csvMultipleWarning",
                "CSV is a flat format: your sections will be concatenated in a single file, each preceded by a section header row. For a per-tab layout, pick Excel."
              )}
            </div>
          )}
        </div>

        {selected.length === 0 && (
          <CustomTypography
            text={t(
              "fleetReports.noSelection",
              "Select at least one section to generate the report."
            )}
            variant="link"
          />
        )}
      </div>
    </CustomModal>
  );
};
